/**
 * ShiftOverview.js
 * Live-Übersicht der aktuellen Schicht mit Einnahmen und Pausen
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Banknote,
  CreditCard,
  Globe,
  Gift,
  Clock,
  Package,
  PlayCircle,
  StopCircle,
  RefreshCw,
  Loader2,
  TrendingUp,
  Coffee,
  PauseCircle,
  Timer
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ShiftOverview = ({ driverId, onShiftStart, onShiftEnd }) => {
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [breakLoading, setBreakLoading] = useState(false);

  const fetchShiftData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/drivers/${driverId}/shift/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setShift(data);
    } catch (error) {
      console.error('Shift fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShiftData();
    const interval = setInterval(fetchShiftData, 30000); // Alle 30 Sekunden
    return () => clearInterval(interval);
  }, [driverId]);

  const handleStartShift = async () => {
    setStarting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/drivers/${driverId}/shift/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenant_id: 'tenant_zozo_burger' })
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Schicht gestartet! 🚗');
        fetchShiftData();
        onShiftStart?.(data);
      } else {
        toast.error(data.detail || 'Fehler beim Starten');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setStarting(false);
    }
  };

  const handleBreakToggle = async () => {
    setBreakLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = shift?.on_break 
        ? `/api/drivers/${driverId}/shift/break/end`
        : `/api/drivers/${driverId}/shift/break/start`;
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        if (shift?.on_break) {
          toast.success(`Pause beendet (${data.break_duration_minutes} Min.) ☕`);
        } else {
          toast.success('Pause gestartet ☕');
        }
        fetchShiftData();
      } else {
        toast.error(data.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setBreakLoading(false);
    }
  };

  const formatMinutes = (minutes) => {
    if (!minutes) return '0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const formatDuration = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Keine aktive Schicht
  if (!shift?.active) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-6 text-center">
          <Clock className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Keine aktive Schicht</h3>
          <p className="text-zinc-400 mb-6">
            Starte eine Schicht um Lieferungen anzunehmen und deine Einnahmen zu tracken.
          </p>
          <Button
            onClick={handleStartShift}
            disabled={starting}
            className="bg-green-600 hover:bg-green-700 h-14 text-lg px-8"
          >
            {starting ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <PlayCircle className="h-5 w-5 mr-2" />
            )}
            Schicht starten
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Aktive Schicht
  return (
    <div className="space-y-4">
      {/* Schicht-Status Header */}
      <Card className={`border ${shift?.on_break 
        ? 'bg-gradient-to-r from-amber-900/50 to-amber-800/30 border-amber-700' 
        : 'bg-gradient-to-r from-green-900/50 to-green-800/30 border-green-700'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full animate-pulse ${shift?.on_break ? 'bg-amber-500' : 'bg-green-500'}`} />
              <div>
                <p className={`text-sm ${shift?.on_break ? 'text-amber-400' : 'text-green-400'}`}>
                  {shift?.on_break ? '☕ In Pause' : 'Aktive Schicht'}
                </p>
                <p className="text-white font-bold text-lg">
                  {formatDuration(shift.duration_hours)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchShiftData}
                className={shift?.on_break ? 'text-amber-400' : 'text-green-400'}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onShiftEnd?.(shift)}
                className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
              >
                <StopCircle className="h-4 w-4 mr-1" />
                Beenden
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pausen-Steuerung */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Coffee className={`h-8 w-8 ${shift?.on_break ? 'text-amber-500' : 'text-zinc-500'}`} />
              <div>
                <p className="text-zinc-400 text-sm">Pausenzeit gesamt</p>
                <p className="text-white font-bold text-lg">
                  {formatMinutes(shift?.break_total_minutes || 0)}
                </p>
                {shift?.breaks_count > 0 && (
                  <p className="text-zinc-500 text-xs">{shift.breaks_count} Pause(n)</p>
                )}
              </div>
            </div>
            <Button
              onClick={handleBreakToggle}
              disabled={breakLoading}
              variant={shift?.on_break ? "default" : "outline"}
              className={shift?.on_break 
                ? "bg-amber-600 hover:bg-amber-700" 
                : "border-amber-600 text-amber-500 hover:bg-amber-600 hover:text-white"}
            >
              {breakLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : shift?.on_break ? (
                <PlayCircle className="h-4 w-4 mr-2" />
              ) : (
                <PauseCircle className="h-4 w-4 mr-2" />
              )}
              {shift?.on_break ? 'Weiterarbeiten' : 'Pause'}
            </Button>
          </div>
          
          {/* Netto-Arbeitszeit Anzeige */}
          {shift?.net_work_minutes !== undefined && (
            <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-2 text-sm">
              <Timer className="h-4 w-4 text-zinc-500" />
              <span className="text-zinc-400">Netto-Arbeitszeit:</span>
              <span className="text-white font-medium">{formatMinutes(shift.net_work_minutes)}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lieferungen Counter */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-primary" />
              <div>
                <p className="text-zinc-400 text-sm">Lieferungen</p>
                <p className="text-white font-bold text-2xl">
                  {shift.deliveries?.completed || 0}
                </p>
              </div>
            </div>
            {shift.deliveries?.active > 0 && (
              <Badge className="bg-amber-500">
                {shift.deliveries.active} aktiv
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Einnahmen Übersicht */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Einnahmen heute
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Bar */}
          <div className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg">
            <div className="flex items-center gap-3">
              <Banknote className="h-6 w-6 text-green-500" />
              <span className="text-white">Bar eingenommen</span>
            </div>
            <span className="text-white font-bold text-lg">
              {formatCurrency(shift.payments?.cash_collected)}
            </span>
          </div>

          {/* EC vor Ort */}
          <div className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg">
            <div className="flex items-center gap-3">
              <CreditCard className="h-6 w-6 text-blue-500" />
              <span className="text-white">EC vor Ort (Tap-to-Pay)</span>
            </div>
            <span className="text-white font-bold text-lg">
              {formatCurrency(shift.payments?.card_onsite)}
            </span>
          </div>

          {/* Online bezahlt */}
          <div className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg">
            <div className="flex items-center gap-3">
              <Globe className="h-6 w-6 text-purple-500" />
              <span className="text-white">Online bezahlt</span>
            </div>
            <span className="text-white font-bold text-lg">
              {formatCurrency(shift.payments?.card_online)}
            </span>
          </div>

          {/* Trinkgeld */}
          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-900/30 to-amber-800/20 rounded-lg border border-amber-700/50">
            <div className="flex items-center gap-3">
              <Gift className="h-6 w-6 text-amber-500" />
              <span className="text-amber-200">Trinkgeld</span>
            </div>
            <span className="text-amber-400 font-bold text-lg">
              {formatCurrency(shift.payments?.tips_total)}
            </span>
          </div>

          {/* Summe */}
          <div className="pt-3 border-t border-zinc-700">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Gesamt</span>
              <span className="text-white font-bold text-2xl">
                {formatCurrency(shift.payments?.total)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bar abzugeben */}
      <Card className="bg-gradient-to-r from-red-900/30 to-red-800/20 border-red-700/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-300 text-sm">Bar abzugeben</p>
              <p className="text-white font-bold text-2xl">
                {formatCurrency(shift.cash_to_return)}
              </p>
            </div>
            <Banknote className="h-10 w-10 text-red-500" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShiftOverview;
